export type DiffEntry =
  | { kind: "added"; socketId: string; assets: string[] }
  | { kind: "removed"; socketId: string; assets: string[] }
  | {
      kind: "changed";
      socketId: string;
      oldAssets: string[];
      newAssets: string[];
    };

export function arraysEqual(a: string[], b: string[]): boolean {
  if (a.length !== b.length) {
    return false;
  }
  for (let i = 0; i < a.length; i++) {
    if (a[i] !== b[i]) {
      return false;
    }
  }
  return true;
}

export function computeDiff(
  serverMap: Record<string, string[]>,
  draftMap: Record<string, string[]>,
): DiffEntry[] {
  const entries: DiffEntry[] = [];
  for (const [socketId, assets] of Object.entries(draftMap)) {
    if (!(socketId in serverMap)) {
      entries.push({ kind: "added", socketId, assets });
    } else {
      const old = serverMap[socketId];
      if (!arraysEqual(old, assets)) {
        entries.push({
          kind: "changed",
          socketId,
          oldAssets: old,
          newAssets: assets,
        });
      }
    }
  }
  for (const [socketId, assets] of Object.entries(serverMap)) {
    if (!(socketId in draftMap)) {
      entries.push({ kind: "removed", socketId, assets });
    }
  }
  return entries;
}

export type CsvParseResult =
  | { ok: true; map: Record<string, string[]> }
  | { ok: false; errors: string[] };

const CSV_COLUMNS = ["socket_id", "connector_id", "asset_id"] as const;

/** Parses a `socket_id,connector_id,asset_id` CSV into a socket-to-asset map.
 * Accepts `,` or `;` as separator (Excel uses `;` in many EU locales) and an
 * optional header row. Connector IDs set the asset order within a socket and
 * must run 1..n without gaps. */
export function parseSocketMappingCsv(
  text: string,
  knownAssetIds: string[],
): CsvParseResult {
  const lines = text
    .replace(new RegExp("^\\uFEFF"), "")
    .split(/\r?\n/)
    .map((line, index) => ({ line: line.trim(), lineNumber: index + 1 }))
    .filter(({ line }) => line !== "");

  if (lines.length === 0) {
    return { ok: false, errors: ["The file is empty."] };
  }

  const separator = lines[0].line.includes(";") ? ";" : ",";
  const splitLine = (line: string) =>
    line.split(separator).map((cell) => cell.trim().replace(/^"(.*)"$/, "$1"));

  const firstCells = splitLine(lines[0].line).map((c) => c.toLowerCase());
  const hasHeader = CSV_COLUMNS.every((col, i) => firstCells[i] === col);
  const rows = hasHeader ? lines.slice(1) : lines;

  const errors: string[] = [];
  const known = new Set(knownAssetIds);
  const connectorsBySocket = new Map<string, Map<number, string>>();
  const socketByAsset = new Map<string, string>();

  for (const { line, lineNumber } of rows) {
    const cells = splitLine(line);
    if (cells.length !== CSV_COLUMNS.length) {
      errors.push(
        `Line ${lineNumber}: expected ${CSV_COLUMNS.length} columns (${CSV_COLUMNS.join(separator)}), got ${cells.length}.`,
      );
      continue;
    }
    const [socketId, connectorText, assetId] = cells;
    const connectorId = Number(connectorText);
    if (!socketId || !assetId) {
      errors.push(`Line ${lineNumber}: socket_id and asset_id are required.`);
      continue;
    }
    if (!Number.isInteger(connectorId) || connectorId < 1) {
      errors.push(
        `Line ${lineNumber}: connector_id "${connectorText}" must be a whole number of 1 or more.`,
      );
      continue;
    }
    if (!known.has(assetId)) {
      errors.push(
        `Line ${lineNumber}: asset "${assetId}" is not in this site's configuration.`,
      );
      continue;
    }
    const otherSocket = socketByAsset.get(assetId);
    if (otherSocket !== undefined) {
      errors.push(
        `Line ${lineNumber}: asset "${assetId}" is already mapped to socket "${otherSocket}".`,
      );
      continue;
    }
    const connectors = connectorsBySocket.get(socketId) ?? new Map();
    if (connectors.has(connectorId)) {
      errors.push(
        `Line ${lineNumber}: socket "${socketId}" has connector ${connectorId} more than once.`,
      );
      continue;
    }
    connectors.set(connectorId, assetId);
    connectorsBySocket.set(socketId, connectors);
    socketByAsset.set(assetId, socketId);
  }

  const map: Record<string, string[]> = {};
  for (const [socketId, connectors] of connectorsBySocket) {
    const ids = Array.from(connectors.keys()).sort((a, b) => a - b);
    if (ids.some((id, i) => id !== i + 1)) {
      errors.push(
        `Socket "${socketId}": connector IDs must run from 1 without gaps, got ${ids.join(", ")}.`,
      );
      continue;
    }
    map[socketId] = ids.map((id) => connectors.get(id)!);
  }

  if (errors.length === 0 && Object.keys(map).length === 0) {
    errors.push("The file has no mapping rows.");
  }
  return errors.length > 0 ? { ok: false, errors } : { ok: true, map };
}
